import { Typography, List } from '@mui/material';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import SupervisedUserCircleOutlinedIcon from '@mui/icons-material/SupervisedUserCircleOutlined';
import { ListCheck } from '@globalvisioninc/assets/icons';
import { useLocation } from 'react-router-dom';
import { BookmarksOutlined, LockRounded, FolderShared } from '@mui/icons-material';
import {
  canAccessAuditTrail,
  isSuperAdmin,
  isAdmin,
  isTenantAdmin,
  isTenantManager,
  isCustomProfiler,
} from 'utils/auth';
import { useFlags } from 'launchdarkly-react-client-sdk';
import useIsInternalOrg from 'hooks/useIsInternalOrg';
import MenuListItem from './DropdownMenuItem';
import { useUserStateStore } from 'zstore/userStateStore';
import { useSuggestedWords } from 'store/queries/dictionaries/suggestedWords';
import GVChip, { GVChipColor, GVChipSize } from 'components/GVChip/GVChip';
import CustomStatusesIcon from 'components/icons/CustomStatuses/CustomStatusesIcon';
import styles from './DropdownMenu.module.css';

const MyAccountMenu = () => {
  const location = useLocation();
  const featureFlags = useFlags();
  const { user } = useUserStateStore();
  const { suggestedWordsList } = useSuggestedWords();
  const isInternalOrg = useIsInternalOrg();

  if (!user) return null;
  const superPermissions = isSuperAdmin(user);
  const adminPermissions = superPermissions || isAdmin(user) || isTenantAdmin(user);
  const auditTrailPermissions = canAccessAuditTrail(user);
  const tenantPermissions = superPermissions || isTenantManager(user);
  const customProfilePermissions =
    isCustomProfiler(user) ||
    ((isAdmin(user) || isTenantAdmin(user)) && (isInternalOrg || featureFlags.showUsersCustomProfiles));

  const suggestedWordsCount = suggestedWordsList.length;
  const displayBreadcrumb = adminPermissions && suggestedWordsCount > 0;
  return (
    <div className={styles.root}>
      <div className={styles.title}>
        <Typography variant="h4" id="my-account">
          My Account
        </Typography>
      </div>
      <List className={styles.list}>
        <MenuListItem
          test_id="tab_settings"
          text="Settings"
          url="settings"
          icon={<SettingsOutlinedIcon />}
          selected={!!location.pathname.includes('settings')}
        />
        {adminPermissions && (
          <MenuListItem
            test_id="tab_my_team"
            text="My Team"
            url="myteam"
            icon={<AccountCircleOutlinedIcon />}
            selected={!!location.pathname.includes('myteam')}
          />
        )}
        {auditTrailPermissions && (
          <MenuListItem
            test_id="tab_audit_trail"
            text="Audit Trail"
            url="audittrail"
            icon={<ListCheck />}
            selected={!!location.pathname.includes('audittrail')}
          />
        )}
        {tenantPermissions && (
          <MenuListItem
            test_id="tab_tenant_management"
            text="Tenant Management"
            url="tenantmanagement"
            icon={<SupervisedUserCircleOutlinedIcon />}
            selected={!!location.pathname.includes('tenantmanagement')}
          />
        )}
        {adminPermissions && (
          <>
            <MenuListItem
              test_id="tab_custom_dictionaries"
              text="Custom Dictionaries"
              url="customdictionaries"
              icon={
                <>
                  {displayBreadcrumb && !location.pathname.includes('customdictionaries') && (
                    <GVChip className={styles.chip} chipSize={GVChipSize.XSMALL} chipColor={GVChipColor.ALERT} circle />
                  )}
                  <BookmarksOutlined />
                </>
              }
              selected={location.pathname.includes('customdictionaries')}
            />
            <MenuListItem
              test_id="tab_custom_statuses"
              text="Custom Statuses"
              url="customstatuses"
              icon={<CustomStatusesIcon />}
              selected={location.pathname.includes('customstatuses')}
            />
          </>
        )}
        {superPermissions && (
          <MenuListItem
            test_id="tab_internal_api"
            text="API"
            url="internalapi"
            icon={<LockRounded />}
            selected={!!location.pathname.includes('internalapi')}
          />
        )}
        {customProfilePermissions && (
          <MenuListItem
            test_id="tab_custom_profiles"
            text="Custom Profiles"
            url="customprofiles"
            icon={<FolderShared />}
            selected={!!location.pathname.includes('customprofiles')}
          />
        )}
      </List>
      <div className={styles.version}>
        <div className={styles.bar}>
          <Typography className={styles.text} data-testid="app-version">
            App Version {import.meta.env.VITE_VERIFY_APP_VERSION || 'N/A'}
          </Typography>
        </div>
      </div>
    </div>
  );
};

export default MyAccountMenu;
